import { ISelectOption, ISelectProps, Loading, Pullout, PulloutType, Select } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import HtmlParser from 'html-react-parser';
import ContentConstants from '../../../contentConstants';
import {
	AudienceSourceType,
	IAudienceSourceVm,
	IHierarchyMetadata,
	IHrisFileListItem,
	IValidationCollection,
	SurveyPropertyUnderValidation,
} from '../../../models';
import { TFetchableEntity } from '../../../models/IFetchableEntity';
import { AudienceSourceTypeHelper, ValidationCollectionHelper } from '../../../utilities';
import { ClearDiv, DisplayTextWithDetails, LoadingWrapper } from '../../common';

export interface IEmployeeDataSourceProps {
	audienceSource: IAudienceSourceVm;
	hierarchyMetadatas: TFetchableEntity<IHierarchyMetadata[]>;
	hrisFiles: TFetchableEntity<IHrisFileListItem[]>;
	validationResults?: IValidationCollection;
	showChangeWarning: boolean;
	GetHrisListForClient: () => void;
	GetHierarchies: () => void;
	HandleAudienceSourceType: (audienceSourceType?: AudienceSourceType) => void;
	HandleExternalSourceChange: (audienceSourceType: AudienceSourceType, id: string) => void;
}

export interface IEmployeeDataSourceState {
	audienceSourceTypes: ISelectOption[];
	validation: ValidationCollectionHelper<SurveyPropertyUnderValidation>;
}

export class EmployeeDataSource extends React.Component<IEmployeeDataSourceProps, IEmployeeDataSourceState> {
	constructor(props: IEmployeeDataSourceProps) {
		super(props);

		this.state = {
			audienceSourceTypes: AudienceSourceTypeHelper.getAudienceSourceTypes(),
			validation: new ValidationCollectionHelper<SurveyPropertyUnderValidation>(this.props.validationResults),
		};

		this.handleAudienceSourceChange = this.handleAudienceSourceChange.bind(this);
		this.handleAudienceSourceTypeChange = this.handleAudienceSourceTypeChange.bind(this);
	}

	private get Warning() {
		const { audienceSource } = this.props;
		const { audienceSourceType } = audienceSource;

		return {
			...(this.audienceSourceOptions !== 'Loading' && {
				...(this.props.showChangeWarning && {
					'Saved selection changed': ContentConstants.EMPLOYEEDATASOURCE_CHANGEWARNING_MESSAGE,
				}),

				...(this.audienceSourceSel &&
					!this.audienceSourceOptions.map((option) => option.name).includes(this.audienceSourceSel) && {
						'Source Selection N/A': ContentConstants.EMPLOYEEDATASOURCE_NOTAVAIL_WARNING,
					}),

				...((this.audienceSourceOptions.length ?? 0) === 0 && {
					'Source Options N/A':
						audienceSourceType &&
						{
							[AudienceSourceType.Hris]: ContentConstants.EMPLOYEEDATASOURCE_NOHRISFILEOPTIONS_ERRORMESSAGE,
							[AudienceSourceType.Hierarchy]: ContentConstants.EMPLOYEEDATASOURCE_NOHIERARCHYFILE_ERRORMESSAGE,
							[AudienceSourceType.FileUpload]: '',
						}[audienceSourceType],
				}),
			}),
		};
	}

	private get audienceSourceOptions(): ISelectOption[] | 'Loading' {
		const { audienceSource } = this.props;
		const { audienceSourceType } = audienceSource;

		if (audienceSourceType === AudienceSourceType.Hris) {
			const { hrisFiles } = this.props;

			if (hrisFiles.isLoading) {
				return 'Loading';
			}

			if (hrisFiles.couldNotFetchData) {
				return [];
			}

			return hrisFiles.map((hris) => ({
				name: hris.hrisId,
				disabled: hris.hierarchyCount === 0,
				displayText: (
					<DisplayTextWithDetails
						displayText={hris.fileName}
						details={`(${hris.hierarchyCount} hierarchies, ${hris.columnCount} attributes)`}
					/>
				),
			}));
		}

		if (audienceSourceType === AudienceSourceType.Hierarchy) {
			const { hierarchyMetadatas } = this.props;

			if (hierarchyMetadatas.isLoading) {
				return 'Loading';
			}

			if (hierarchyMetadatas.couldNotFetchData) {
				return [];
			}

			return hierarchyMetadatas.map((hierarchy) => ({
				name: hierarchy.hierarchyId,
				displayText: hierarchy.name,
			}));
		}

		return [];
	}

	private get audienceSourceSel() {
		const { audienceSource } = this.props;
		const { audienceSourceType } = audienceSource;

		if (audienceSourceType === AudienceSourceType.Hris) {
			return audienceSource.externalHrisId;
		}

		if (audienceSourceType === AudienceSourceType.Hierarchy) {
			return audienceSource.externalHierarchyId;
		}
	}

	public componentDidMount() {
		this.fetchAudienceSourceOptions();
	}

	public componentDidUpdate(prevProps: IEmployeeDataSourceProps) {
		const { audienceSourceType } = this.props.audienceSource;
		if (audienceSourceType && audienceSourceType !== prevProps.audienceSource.audienceSourceType) {
			this.fetchAudienceSourceOptions();
		}

		if (this.props.validationResults !== prevProps.validationResults) {
			this.setState({
				...this.state,
				validation: new ValidationCollectionHelper<SurveyPropertyUnderValidation>(this.props.validationResults),
			});
		}
	}

	public render() {
		const { audienceSource } = this.props;
		const { audienceSourceType } = audienceSource;
		const { audienceSourceTypes } = this.state;

		return (
			audienceSourceTypes.length && (
				<>
					{((warning) =>
						warning && (
							<>
								<Pullout type={PulloutType.Warning}>{HtmlParser(warning)}</Pullout>
								<ClearDiv height='10' />
							</>
						))(this.Warning['Saved selection changed'] || this.Warning['Source Selection N/A'])}

					<Select
						displayText={ContentConstants.EMPLOYEEDATASOURCE_CHOOSE_YOUR_SOURCE}
						selectSize='xlg'
						items={audienceSourceTypes}
						selectedValue={audienceSourceType?.toString() ?? ''}
						onApply={this.handleAudienceSourceTypeChange}
					/>
					<ClearDiv height={'20'} />
					{this.audienceSourceSelector()}

					{this.Warning['Source Options N/A'] && (
						<>
							<ClearDiv height='10' />
							<Pullout type={PulloutType.Warning}>{HtmlParser(this.Warning['Source Options N/A'])}</Pullout>
						</>
					)}
				</>
			)
		);
	}

	private handleAudienceSourceTypeChange(value?: string | string[]) {
		let audienceSourceType: AudienceSourceType | undefined;
		if (value && value !== '') {
			audienceSourceType = Number(value);
		}
		this.props.HandleAudienceSourceType(audienceSourceType);
	}

	private handleAudienceSourceChange(values?: string | string[] | undefined) {
		const { audienceSourceType } = this.props.audienceSource;

		if (audienceSourceType) {
			const id = values as string;
			this.props.HandleExternalSourceChange(audienceSourceType, id);
		}
	}

	private fetchAudienceSourceOptions() {
		const { audienceSourceType } = this.props.audienceSource;

		switch (audienceSourceType) {
			case AudienceSourceType.Hris:
				this.props.GetHrisListForClient();
				break;
			case AudienceSourceType.Hierarchy:
				this.props.GetHierarchies();
				break;
			case AudienceSourceType.FileUpload:
			default:
				break;
		}
	}

	private audienceSourceSelector() {
		const { audienceSource } = this.props;
		const { audienceSourceType } = audienceSource;
		const { validation } = this.state;

		// If no audience source type is specified, or we are sourcing an audience via file upload
		if (!audienceSourceType || audienceSourceType === AudienceSourceType.FileUpload) {
			// Do not render a selector
			return undefined;
		}

		if (this.audienceSourceOptions === 'Loading') {
			return (
				<LoadingWrapper>
					<Loading height='35px' width='35px' />
				</LoadingWrapper>
			);
		}

		const selectorProps: ISelectProps = {
			className: {
				[AudienceSourceType.Hris]: 'hris-options',
				[AudienceSourceType.Hierarchy]: 'hierarchy-options',
			}[audienceSourceType],

			displayText: ContentConstants.EMPLOYEEDATASOURCE_CHOOSE_YOUR_FILE,
			selectSize: 'xlg',

			items: this.audienceSourceOptions,
			selectedValue: this.audienceSourceSel,

			error: !validation.IsValid(SurveyPropertyUnderValidation.audienceSource),
			message: validation.Errors(SurveyPropertyUnderValidation.audienceSource),

			onApply: this.handleAudienceSourceChange,
		};

		return <Select {...selectorProps} />;
	}
}
